import { Form, Input, Button } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowStep, type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

/** Matches WorkflowStep.MaxThankYouProductTitleLength, itself pinned to the workflow
 *  title cap so the prefilled default always saves. */
const MAX_PRODUCT_TITLE = 150;

type Props = {
  templateId: string;
  /** Full template — the per-language defaults are its own title. */
  workflow: WorkflowTemplate;
  /** The ThankYou step being configured; null after a refresh removed it. */
  step: WorkflowStep | null;
  editable: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent refreshes the template. */
  onChanged: () => Promise<void>;
};

type FormValues = {
  productTitleEn: string;
  productTitleFr?: string;
};

/**
 * Thank You step config modal — the product name interpolated into the confirmation
 * headline, per language. First open on an unconfigured step prefills from the
 * workflow's own title, which is also what the runtime falls back to, so the
 * originator starts from exactly what the advisor would otherwise see.
 *
 * Shorter than its Welcome sibling by design: the sentence around the product name
 * forks by audience (advisor vs public customer) and stays in the i18n bundle, so
 * there is nothing else here to author yet.
 */
export function ThankYouConfigModal({
  templateId,
  workflow,
  step,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const open = step !== null;
  // Seed once per open session, keyed on the step id. The parent re-resolves `step`
  // and `workflow` on every template refresh — including the 20s editor-lock poll —
  // which changes these object references with no real content change. Without the
  // guard that background refresh wipes the originator's unsaved edits. Same reasoning
  // as WelcomeConfigModal, and the same bug if it is dropped.
  const seededStepId = useRef<string | null>(null);

  useEffect(() => {
    if (!step) {
      seededStepId.current = null;
      form.resetFields();
      return;
    }
    if (seededStepId.current === step.id) return;
    seededStepId.current = step.id;
    const cfg = step.thankYouConfig;
    form.setFieldsValue(
      cfg
        ? { productTitleEn: cfg.productTitleEn, productTitleFr: cfg.productTitleFr }
        : // Never configured — seed from the workflow title, which is the runtime
          // fallback. French is left genuinely blank when the workflow has no French
          // name rather than borrowing the English one: a blank field still falls back
          // per-language at runtime, while an English name parked in a French box
          // would be saved and shown inside French copy.
          { productTitleEn: workflow.nameEn, productTitleFr: workflow.nameFr ?? '' },
    );
  }, [step, form, workflow]);

  const handleSave = async () => {
    if (!step) return;
    // validateFields REJECTS on invalid input rather than returning a result, so the await
    // has to be guarded: unguarded it escapes as an unhandled promise rejection every time
    // the originator saves with the English name blank. Nothing to report in the catch —
    // antd has already rendered the per-field error, which is the whole feedback.
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await workflowsApi.setThankYouConfig(templateId, step.id, {
        productTitleEn: values.productTitleEn.trim(),
        productTitleFr: (values.productTitleFr ?? '').trim(),
      });
      // No companion editStep call here, unlike WelcomeConfigModal. That modal renames
      // the step so the rail label tracks the landing-card title; this one must not —
      // the rail reads "Thank You" / "Merci", which is the step's name, not the product.
      message.success(t('workflow.steps.thankYou.saved'));
      await onChanged();
      onClose();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.thankYou.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BmoModal
      open={open}
      onCancel={onClose}
      title={t('workflow.steps.thankYou.modalTitle')}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!editable}
          onClick={handleSave}
        >
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false} disabled={!editable}>
        <Form.Item
          name="productTitleEn"
          label={t('workflow.steps.thankYou.productTitleEn')}
          extra={t('workflow.steps.thankYou.productTitleHint')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('workflow.steps.thankYou.productTitleRequired'),
            },
          ]}
        >
          <Input maxLength={MAX_PRODUCT_TITLE} showCount />
        </Form.Item>
        <Form.Item
          name="productTitleFr"
          label={t('workflow.steps.thankYou.productTitleFr')}
          extra={t('workflow.steps.thankYou.productTitleFrHint')}
        >
          <Input maxLength={MAX_PRODUCT_TITLE} showCount />
        </Form.Item>
      </Form>
    </BmoModal>
  );
}
